import {Component, EventEmitter, OnInit, Output, ViewChild} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {Pagination} from "@models/pagination.model";
import {ToastrService} from "ngx-toastr";
import {Patient} from "@shared/api/models/query/patient.model";
import {FormsService} from "@shared/api/services/v2/forms.service";
import {Form, FormResult} from "@shared/api/models/query/form.model";
import {QuestionnaireFormComponent} from "@shared/components/questionnaire-form/questionnaire-form.component";
import {PatientFormResultsService} from "@shared/api/services/v2/patient-form-results.service";
import {PatientFormResultUpdate} from "@shared/api/models/command/patient-form-result.model";
import {PatientFormResult} from "@shared/api/models/query/patient-form-result.model";
import {PatientsService} from "@shared/api/services/v2/patients.service";
import {DatePipe, NgIf} from "@angular/common";
import {SpinnerComponent} from "@shared/components/spinner/spinner.component";
import {StageDTO} from "@cdss/pages/patient-registration/care-programme/module-item.dto";
import {UserCareProgram} from "@shared/api/models/query/user-care-program.model";
import {CareProgramModule} from "@shared/api/models/query/care-program.model";
import {CareProgramModulesService} from "@shared/api/services/v2/care-program-modules.service";
import {UserCareProgramsService} from "@shared/api/services/v2/user-care-programs.service";
import {SensitiveDataMaskingConfig} from "@shared/config/sensitive-data-masking.config";
import {
    PatientRegistrationConfirmationCareProgrammeComponent
} from "@cdss/pages/patient-registration/confirmation/patient-registration-confirmation-care-programme.component";

@Component({
    selector: 'patient-registration-confirmation',
    imports: [
        ReactiveFormsModule,
        QuestionnaireFormComponent,
        FormsModule,
        NgIf,
        SpinnerComponent,
        DatePipe,
        PatientRegistrationConfirmationCareProgrammeComponent,
    ],
    standalone: true,
    templateUrl: './patient-registration-confirmation.component.html'
})
export class PatientRegistrationConfirmationComponent implements OnInit {
    @ViewChild(QuestionnaireFormComponent) questionnaireFormComponent: QuestionnaireFormComponent
    @ViewChild("patientCareProgram") patientCareProgramComponent: PatientRegistrationConfirmationCareProgrammeComponent
    @ViewChild("carerCareProgram") carerCareProgramComponent: PatientRegistrationConfirmationCareProgrammeComponent

    @Output() output = new EventEmitter<string>();

    loading: boolean = false
    pagination: Pagination = new Pagination()
    typeOfUser: string
    patient: Patient
    form: Form
    /**
     * UNIVAPLAT-3719: the patient as persisted after the care-programme step, which is where the
     * "Your child" values are saved. `patient` above is the state from before that step.
     */
    childPatient?: Patient


    constructor(
        private toastr: ToastrService,
        private formsService: FormsService,
        private patientFormResultsService: PatientFormResultsService,
        private patientsService: PatientsService,
        private maskingConfig: SensitiveDataMaskingConfig,
    ) {
    }

    /** "Your child" is only for the carer-only type, and only when the programme has a child mode. */
    get showChildSection(): boolean {
        if (this.typeOfUser !== 'Carer') {
            return false
        }
        const mode = this.carerCareProgramComponent?.userCareProgram?.careProgram?.childMode
        return mode === 'required' || mode === 'optional'
    }

    /** The stored name can come back masked: it is never shown as if it were the real value. */
    get childNameHidden(): boolean {
        return this.maskingConfig.enabled
    }

    private async loadChildPatient() {
        try {
            this.childPatient = await this.patientsService.getById(this.patient.id)
        } catch {
            // The child's details are optional on this screen; the rest of the summary still works.
            // Nothing is logged: the payload is personal data.
            this.childPatient = undefined
        }
    }

    ngOnInit(): void {
    }

    init(patient: Patient, typeOfUser: string) {
        this.patient = patient
        this.typeOfUser = typeOfUser
        this.childPatient = undefined
        if (typeOfUser === 'Carer') {
            void this.loadChildPatient()
        }

        const pagination = new Pagination();
        pagination.filter = {
            type: "co-morbidities",
            scope: "cdss",
            target: typeOfUser
        }
        this.formsService.listPaginated(pagination).subscribe({
            next: async response => {
                this.form = response.data[0]
                this.patientFormResultsService.get(this.patient.id, this.form.id).subscribe({
                    next: (result?: PatientFormResult) => {
                        this.questionnaireFormComponent.init(this.form, result?.questions)
                        if (['Patient', 'Patient and carer'].includes(this.typeOfUser)) {
                            this.patientCareProgramComponent.init(this.patient.user)
                        }
                        if (['Carer', 'Patient and carer'].includes(this.typeOfUser)) {
                            this.carerCareProgramComponent.init(this.patient.carer.user)
                        }
                    }
                })
            }
        })

    }


    submit() {
        this.loading = true
        this.patientsService.update(this.patient.id, {wizardStep: 5, status: "onboarding"}).subscribe({
            next: () => {
                this.loading = false
                this.output.emit("next")
            },
            error: error => {
                this.loading = false
                this.toastr.error(error.error.exception, error.error.error);
            }
        })
    }

    protected prev() {
        this.output.emit("prev")
    }
}
